'use client'

import React, { useState } from 'react';
import type { BrandEquityNarrative } from '../../types';

interface NarrativeSectionProps {
  narrative?: BrandEquityNarrative
  onRegenerate: () => Promise<void>
}

const SECTIONS = [
  { key: 'why' as const, label: 'Why the Score Moved', icon: 'ri-question-line' },
  { key: 'where' as const, label: 'Where the Impact Came From', icon: 'ri-arrow-right-line' },
  { key: 'who' as const, label: 'Who Engaged Most', icon: 'ri-user-line' },
  { key: 'competitor' as const, label: 'Competitor Comparison', icon: 'ri-bar-chart-grouped-line' },
  { key: 'what_next' as const, label: 'What to Do Next', icon: 'ri-lightbulb-line' },
  { key: 'expected_impact' as const, label: 'Expected Impact', icon: 'ri-line-chart-line' },
]

export default function NarrativeSection({ narrative, onRegenerate }: NarrativeSectionProps) {
  const [isRegenerating, setIsRegenerating] = useState(false)

  const handleRegenerate = async () => {
    setIsRegenerating(true)
    try {
      await onRegenerate()
    } finally {
      setIsRegenerating(false)
    }
  }

  if (!narrative) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
        <i className="ri-file-text-line text-3xl text-gray-300 mb-2"></i>
        <p className="text-sm text-gray-500">No narrative available</p>
        <p className="text-xs text-gray-400 mt-1">Generate your first report to get AI insights</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <i className="ri-sparkling-2-line text-indigo-500"></i>
          <h3 className="text-sm font-semibold text-gray-900">Kiki Insights</h3>
          {narrative._fallback && (
            <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
              Template
            </span>
          )}
        </div>
        {narrative._fallback && (
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1 disabled:opacity-50"
          >
            {isRegenerating ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-refresh-line"></i>
            )}
            Regenerate
          </button>
        )}
      </div>

      <div className="divide-y divide-gray-50">
        {SECTIONS.map(({ key, label, icon }) => {
          const text = narrative[key]
          if (!text || text === 'Data not available for this section.') {
            return null
          }
          return (
            <div key={key} className="px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <i className={`${icon} text-indigo-500 text-xs`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-medium text-gray-500 mb-1">{label}</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
